import { AiChatMessage } from '../components/ai-chat/ai-chat.component';
import { AudioExperienceId, ListeningTimeId, TimeForReadingId, VisionCareId } from './quiz';

declare const $localize: any;

export type QuizAnswers = { [key: string]: unknown };

// A result screen's messages, and the properties of the metric event it reports once (see
// FunnelFlowService.trackResultMetricOnce).
export interface ResultMessages {
  messages: AiChatMessage[];
  metricProperties: Record<string, unknown>;
}

export interface ListeningPlan {
  dailyLabel: string;
  progressOutcome: string;
  potentialLift: number;
}

export interface MomentumScore {
  currentValue: number;
  potentialValue: number;
}

export interface ListPhrases {
  two(first: string, second: string): string;
  many(items: string, last: string): string;
}

export const listeningPlans: Record<ListeningTimeId, ListeningPlan> = {
  minutes0515: {
    dailyLabel: $localize`:@@kindleResultPlan0515:5-15 focused minutes a day`,
    progressOutcome: $localize`:@@kindleResultOutcome0515:restart books without making reading feel like another task`,
    potentialLift: 24,
  },
  minutes1530: {
    dailyLabel: $localize`:@@kindleResultPlan1530:15-30 minutes a day`,
    progressOutcome: $localize`:@@kindleResultOutcome1530:turn several spare moments each week into chapter progress`,
    potentialLift: 29,
  },
  minutes3060: {
    dailyLabel: $localize`:@@kindleResultPlan3060:30-60 minutes a day`,
    progressOutcome: $localize`:@@kindleResultOutcome3060:move through long books steadily, even on busy days`,
    potentialLift: 34,
  },
  hours123: {
    dailyLabel: $localize`:@@kindleResultPlan123:1-2 hours a day`,
    progressOutcome: $localize`:@@kindleResultOutcome123:make deep progress on books you have been meaning to finish`,
    potentialLift: 38,
  },
  hours2plus: {
    dailyLabel: $localize`:@@kindleResultPlan2plus:2+ hours a day`,
    progressOutcome: $localize`:@@kindleResultOutcome2plus:build a serious listening routine for a large Kindle backlog`,
    potentialLift: 40,
  },
};

const timeForReadingScore: Record<TimeForReadingId, number> = {
  allTheTime: -6,
  sometimes: -2,
  notReally: 2,
};

const visionCareScore: Record<VisionCareId, number> = {
  gettingWorse: -3,
  aBit: -1,
  allGood: 0,
};

const audioExperienceScore: Record<AudioExperienceId, number> = {
  yesRegularly: 5,
  yesOccasionally: 3,
  noButInterested: 1,
  noNotReally: -2,
};

const kindleListPhrases: ListPhrases = {
  two: (first, second) =>
    $localize`:@@kindleResultTwoItemList:${first}:FIRST: and ${second}:SECOND:`,
  many: (items, last) => $localize`:@@kindleResultMultiItemList:${items}:ITEMS:, and ${last}:LAST:`,
};

export function isAllowedValue<T extends string>(
  value: unknown,
  allowedValues: Record<T, unknown>
): value is T {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(allowedValues, value);
}

// The answer's ids that are still options, so a stale or tampered stored answer drops out.
export function getAnswerIds<T extends string>(
  answers: QuizAnswers,
  key: string,
  allowedValues: Record<T, unknown>
): T[] {
  const answer = answers[key];
  const ids = Array.isArray(answer) ? answer : typeof answer === 'string' ? [answer] : [];

  return ids.filter((id): id is T => isAllowedValue(id, allowedValues));
}

export function getFirstAnswerId<T extends string>(
  answers: QuizAnswers,
  key: string,
  allowedValues: Record<T, unknown>
): T | undefined {
  return getAnswerIds(answers, key, allowedValues)[0];
}

export function hasEyeComfortConcern(
  visionCareId: VisionCareId | undefined
): visionCareId is 'gettingWorse' | 'aBit' {
  return visionCareId === 'gettingWorse' || visionCareId === 'aBit';
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// "a", "a and b", or "a, b, and c" - anything past the third item is dropped.
export function formatList(items: string[], phrases: ListPhrases = kindleListPhrases): string {
  if (items.length <= 1) {
    return items[0] || '';
  }

  if (items.length === 2) {
    return phrases.two(items[0], items[1]);
  }

  const visibleItems = items.slice(0, 3);

  return phrases.many(visibleItems.slice(0, -1).join(', '), visibleItems[visibleItems.length - 1]);
}

// The Reading/Story Momentum Score: each friction costs 3 points (12 at most), the time, eyesight
// and audio answers move it further, and the plan's lift sets where it can get to.
export function getMomentumScore(params: {
  frictionCount: number;
  timeForReadingId?: TimeForReadingId;
  visionCareId?: VisionCareId;
  audioExperienceId?: AudioExperienceId;
  potentialLift: number;
}): MomentumScore {
  const { timeForReadingId, visionCareId, audioExperienceId } = params;
  const score =
    64 -
    Math.min(params.frictionCount * 3, 12) +
    (timeForReadingId ? timeForReadingScore[timeForReadingId] : 0) +
    (visionCareId ? visionCareScore[visionCareId] : 0) +
    (audioExperienceId ? audioExperienceScore[audioExperienceId] : 0);
  const currentValue = clamp(Math.round(score), 43, 68);

  return {
    currentValue,
    potentialValue: clamp(currentValue + params.potentialLift, 72, 94),
  };
}
